import React, { useMemo, useState } from 'react';
import { FiGrid } from 'react-icons/fi';
import Navbar from '../Nav';
import Project from './Project';
import './Main.css';

const featuredProjects = [
  {
    name: 'Hurricane Prediction',
    org: 'RBC x UNICEF — Borealis AI mentorship',
    description: 'Iterating ML models to forecast hurricane behavior and prioritize recall; refining data hygiene and feature pipelines.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['PyTorch', 'Python', 'MLOps'],
    tag: 'Applied ML',
    accent: 'applied'
  },
  {
    name: 'RL Locomotion Rewards',
    org: 'UAlberta x Mitsubishi',
    description: 'Bayesian-optimized rewards and CUDA-parallel PPO for faster, more stable locomotion.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['IsaacLab', 'Gym', 'CUDA', 'PPO'],
    tag: 'Research',
    accent: 'research'
  },
  {
    name: 'Fault-Tolerant Control',
    org: 'UAlberta x Mitsubishi',
    description: 'LLM-shaped and fault-adaptive rewards for safer robotic reach tasks.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['MuJoCo', 'Gym', 'LLM shaping'],
    tag: 'Research',
    accent: 'research'
  },
  {
    name: 'GenAI Demos',
    org: 'Cognizant',
    description: 'Fine-tuning CNNs and LLMs with RAG validation for wildfire/disaster data; shipping internal demos.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['LLMs', 'CNNs', 'SageMaker'],
    tag: 'Applied ML',
    accent: 'applied'
  },
  {
    name: 'API Simulators & Observability',
    org: 'Canadian Imperial Bank of Commerce',
    description: 'Spring-based simulators and SSL automation to de-risk APIs before prod; tracing/logging under load.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['Spring', 'Java', 'Docker', 'OpenShift'],
    tag: 'Systems',
    accent: 'systems'
  },
  {
    name: 'Taskify',
    org: 'Personal',
    description: 'React Native scheduler with DP engine for weekly optimization and Firebase realtime sync.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['React Native', 'Firebase', 'DP'],
    tag: 'Product',
    accent: 'product'
  },
  {
    name: 'AWS Wildfire Hackathon — 3rd',
    org: 'Hackathon · Jul 2024',
    description: 'LogReg to flag misinformation; LLM report generation via Bedrock; RAG validation on SageMaker.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['LogReg', 'Node/React', 'Bedrock', 'SageMaker'],
    tag: 'Applied ML',
    accent: 'applied'
  },
  {
    name: 'QRCheckIn',
    org: 'Android app',
    description: 'Secure event check-ins with QR generation/scanning plus Firebase Auth/Firestore.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['React Native', 'Firebase', 'QR'],
    tag: 'Product',
    accent: 'product'
  },
  {
    name: 'DineIn',
    org: 'Full-stack app',
    description: 'Reservations, ordering, and check-in with role-based auth; React Bootstrap + Express CRUD.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['React', 'Express', 'Auth'],
    tag: 'Product',
    accent: 'product'
  },
  {
    name: 'JBot',
    org: 'Discord bot',
    description: 'Automated moderation + scraping commands for 1000+ users; hosted on VM.',
    link: 'https://github.com/Afraz0Khan',
    tech: ['Discord API', 'Python', 'Scraping'],
    tag: 'Automation',
    accent: 'automation'
  }
];

function Projects() {
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'research', label: 'Research' },
    { key: 'applied', label: 'Applied ML' },
    { key: 'systems', label: 'Systems' },
    { key: 'product', label: 'Product' },
    { key: 'automation', label: 'Automation' }
  ];

  const filteredProjects = useMemo(() => {
    if (activeFilter === 'all') return featuredProjects;
    return featuredProjects.filter((p) => p.accent === activeFilter);
  }, [activeFilter]);

  return (
    <div className="projects-page">
      <Navbar />
      <div className="projects-wrapper">
        <header className="projects__header">
          <p className="projects__sublabel">Projects & Research</p>
          <h1 className="projects__title">Selected work</h1>
          <p className="projects__subtitle">
            A mix of applied ML/RL research and engineering work. Deep dives cover problem → approach → outcome with metrics and stack notes.
          </p>
          <div className="projects__filters">
            {filters.map((f) => (
              <button
                key={f.key}
                className={`projects__pill ${activeFilter === f.key ? 'projects__pill--active' : ''}`}
                onClick={() => setActiveFilter(f.key)}
                type="button"
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              className="projects__pill projects__pill--clear"
              onClick={() => setActiveFilter('all')}
            >
              Reset filters
            </button>
          </div>
        </header>

        <div className="projects__grid">
          {filteredProjects.map((project) => (
            <Project key={project.name} {...project} />
          ))}
        </div>

        <div className="projects__more">
          <p className="projects__subtitle">
            More repos and experiments live on GitHub. I keep shipping small tools and RL reward explorations there.
          </p>
          <a
            className="projects__pill"
            href="https://github.com/Afraz0Khan?tab=repositories"
            target="_blank"
            rel="noreferrer"
          >
            <FiGrid aria-hidden="true" /> View all GitHub repos
          </a>
        </div>
      </div>
    </div>
  );
}

export default Projects;
